import React, { useCallback, useEffect, useState } from 'react';

import { Dropdown as AntDropdown, Form } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import DownCaretIcon from 'common/Icons/DownCaretIcon';
import useDataQuarterSelection from 'hooks/useDataQuarterSelection';

import { DataQuarterFilterContext } from './DataQuarterFilterContext';
import DataQuarterFilterDropdown from './Dropdown/DataQuarterFilterDropdown';
import { DataQuarterFilterFormValues, DataQuarterSelectionType, QuarterOption } from './types';
import { useCommunityViewContext } from '../../CommunityViewContext';

const StyledDropdown = styled(AntDropdown)`
  font-family: var(--font-regular);
  font-size: 12px;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 12px;
  cursor: pointer;

  color: var(--dark-blue);
  background-color: var(--faded-blue);

  &.active {
    border: solid 1px var(--button-blue);
  }
`;

const BoldLabel = styled.span`
  font-family: var(--font-bold);
  margin-right: 4px;
`;

const getQuarterBySlug = (slug: string | null, quarters: QuarterOption[]) => {
  if (!quarters.length) {
    return undefined;
  }

  if (!slug) {
    return quarters[0];
  }

  return quarters.find((item) => item.slug === slug) || quarters[0];
};

const DataQuarterFilter = () => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<DataQuarterFilterFormValues>();
  const { viewFilters, setViewFilters } = useCommunityViewContext();
  const [selectedQuarterSlug, setSelectedQuarterSlug] = useState<string | null>(null);
  const [includePriorCompetitors, setIncludePriorCompetitors] = useState<boolean>(true);
  const { quarters } = useDataQuarterSelection();

  useEffect(() => {
    if (!viewFilters || !quarters.length) {
      return;
    }

    let slug = quarters[0].slug;
    let includePrior = true;

    const existingDataQuarterSlug = viewFilters.dataQuarterSelection?.selectedQuarterSlug;
    const matchingQuarter = existingDataQuarterSlug ? quarters?.find((q) => q.slug === existingDataQuarterSlug) : null;
    if (matchingQuarter) {
      slug = matchingQuarter.slug;
    }
    const existingIncludePrior = viewFilters.dataQuarterSelection?.includePriorCompetitors;
    if (existingIncludePrior != null) {
      includePrior = existingIncludePrior;
    }

    setSelectedQuarterSlug(slug);
    setIncludePriorCompetitors(includePrior);
    form.setFieldsValue({
      selectedQuarterSlug: slug,
      includePriorCompetitors: includePrior,
    });
  }, [
    form,
    quarters,
    viewFilters?.dataQuarterSelection?.selectedQuarterSlug,
    viewFilters?.dataQuarterSelection?.includePriorCompetitors,
  ]);

  const handleValuesChange = (values: any, allValues: Partial<DataQuarterFilterFormValues>) => {
    if (allValues?.selectedQuarterSlug != null) {
      setSelectedQuarterSlug(allValues.selectedQuarterSlug);
    }
    if (allValues?.includePriorCompetitors != null) {
      setIncludePriorCompetitors(allValues.includePriorCompetitors);
    }
  };

  const handleApply = (values: DataQuarterSelectionType) => {
    if (!setViewFilters) {
      return;
    }

    setSelectedQuarterSlug(values.selectedQuarterSlug || null);
    setIncludePriorCompetitors(values.includePriorCompetitors ?? true);
    setViewFilters({
      ...viewFilters,
      dataQuarterSelection: values,
    });
    setOpen(false);
  };

  const selectedQuarter = getQuarterBySlug(selectedQuarterSlug, quarters);
  const triggerLabel =
    quarters.length && selectedQuarterSlug && selectedQuarterSlug === quarters[0]?.slug
      ? 'Most recent'
      : selectedQuarter?.label;

  const handleReset = useCallback(() => {
    const defaultQuarterSlug = quarters[0]?.slug ?? null;
    form.setFieldsValue({
      selectedQuarterSlug: defaultQuarterSlug,
      includePriorCompetitors: true,
    });
  }, [form, quarters]);

  return (
    <StyledDropdown
      trigger={['click']}
      open={open}
      onOpenChange={(visible) => setOpen(visible)}
      placement="bottomLeft"
      dropdownRender={() => (
        <DataQuarterFilterContext.Provider
          value={{
            quarters,
            form,
            reset: handleReset,
          }}
        >
          <Form
            form={form}
            onFinish={handleApply}
            initialValues={{
              selectedQuarterSlug,
              includePriorCompetitors,
            }}
            onValuesChange={handleValuesChange}
          >
            <DataQuarterFilterDropdown />
          </Form>
        </DataQuarterFilterContext.Provider>
      )}
    >
      <Trigger type="button" className={classNames({ active: open })} onClick={(event) => event.preventDefault()} data-test-id="data-filter">
        <span>
          <BoldLabel>Data{quarters?.length ? ':' : ''}</BoldLabel>
          <span>{triggerLabel}</span>
        </span>
        <DownCaretIcon size={10} alt="" />
      </Trigger>
    </StyledDropdown>
  );
};

export default DataQuarterFilter;
